import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Alert,
    AppHeader,
    DataList,
    ListItemElement,
    Popover,
    ProgressBar,
    RoundButton,
    Searchbox,
    Spinner,
    Tooltip
} from "@tag/tag-components-react-v4";
import {CircleInfoFilled, ClockFilled, DatabaseFilled, DeleteFilled, DownloadFilled, SearchFilled, ServerFilled, StopSignFilled, UserMaleLined} from "@tag/tag-icons";
import {useIsAuthenticated, useMsal} from "@azure/msal-react";
import AppRolesService from "../../General/services/AppRolesService";
import moment from "moment";
import DataExportService from "../services/DataExportService";
import {DataExportJob, DataExportWorker} from "../types/DataExportJob";
import HelperMethods from "../../General/services/HelperMethods";
import {ProjectAccents} from "../../General/config/projectAccents";
import styles from "../CustomerList/CustomerList.module.scss";

const PollIntervalMs = 5000;
// Exports can be started from the customer list or by another user, so the page keeps polling once
// everything has finished, just more slowly, to pick up new exports and the worker slot counts.
const IdlePollIntervalMs = 15000;
const TerminalStatuses = ['Complete', 'Failed', 'Cancelled'];

function DataExports() {

    const {accounts} = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const isAdmin = AppRolesService.isUserAdmin(accounts, isAuthenticated);

    const [exports, setExports] = useState<DataExportJob[]>([]);
    const [workers, setWorkers] = useState<DataExportWorker[]>([]);
    const [cancelling, setCancelling] = useState<string>();
    const [loading, setLoading] = useState<boolean>(true);
    const [downloading, setDownloading] = useState<string>();
    const [deleting, setDeleting] = useState<string>();
    const [alert, setAlert] = useState<{type: any, message: string}>();
    const [searchTerm, setSearchTerm] = useState<string>('');

    const pollTimer = useRef<ReturnType<typeof setTimeout>>();

    const loadExports = useCallback(async () => {
        const [jobs, workerList] = await Promise.all([
            DataExportService.getDataExports(),
            // The worker list is a diagnostic: losing it must not hide the export list.
            DataExportService.getDataExportWorkers().catch(() => [] as DataExportWorker[])
        ]);

        setWorkers(workerList);

        return jobs.map(job => ({
            ...job,
            initials: HelperMethods.getInitials(job.customerName ?? 'Data export'),
            primaryFieldValue: `${job.customerName ?? 'Unknown customer'}${job.clientId ? ` (${job.clientId})` : ''}`
        }));
    }, []);

    // Exports are long running, so the list refreshes itself in the background, quickly while a job
    // is still going and slowly once they have all finished.
    useEffect(() => {
        let cancelled = false;

        const refresh = async () => {
            try {
                const jobs = await loadExports();

                if (cancelled) return;

                setExports(jobs);

                const running = jobs.some(job => !TerminalStatuses.includes(job.status));
                pollTimer.current = setTimeout(refresh, running ? PollIntervalMs : IdlePollIntervalMs);
            } catch {
                if (!cancelled) {
                    setAlert({type: 'error', message: 'Could not load the data exports, please contact HSC Care DevOps'});
                    pollTimer.current = setTimeout(refresh, IdlePollIntervalMs);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        refresh();

        return () => {
            cancelled = true;
            if (pollTimer.current) clearTimeout(pollTimer.current);
        };
    }, [loadExports]);

    const exportsData = useMemo(
        () => searchTerm
            ? HelperMethods.advancedFuseSearch(searchTerm, exports, ['clientId', 'customerName', 'corporateSiteName', 'requestedBy', 'status'])
            : exports,
        [searchTerm, exports]
    );

    const cancel = async (job: DataExportJob) => {
        if (!window.confirm(`Cancel the ${job.exportType} export for ${job.customerName ?? 'this customer'}? It cannot be resumed.`))
            return;

        setCancelling(job.taskId);

        try {
            await DataExportService.cancelDataExport(job.taskId);
            setAlert({type: 'success', message: 'Cancelling the export, the worker will stop it shortly'});
            setExports(await loadExports());
        } catch (e: any) {
            setAlert({type: 'error', message: e.message || 'Could not cancel this export'});
        } finally {
            setCancelling(undefined);
        }
    };

    const busySlots = useMemo(() => workers.reduce((total, worker) => total + worker.busySlots, 0), [workers]);
    const totalSlots = useMemo(() => workers.reduce((total, worker) => total + worker.totalSlots, 0), [workers]);

    const download = async (job: DataExportJob) => {
        setDownloading(job.taskId);

        try {
            const url = await DataExportService.getDataExportDownloadUrl(job.taskId);
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (e: any) {
            setAlert({type: 'error', message: e.message || 'Could not download this export'});
        } finally {
            setDownloading(undefined);
        }
    };

    const remove = async (job: DataExportJob) => {
        if (!window.confirm(`Delete the ${job.exportType} export for ${job.customerName ?? 'this customer'}? Its file is removed from Azure too.`))
            return;

        setDeleting(job.taskId);

        try {
            await DataExportService.deleteDataExport(job.taskId);
            setAlert({type: 'success', message: 'Export and its file deleted'});
            setExports(await loadExports());
        } catch (e: any) {
            setAlert({type: 'error', message: e.message || 'Could not delete this export'});
        } finally {
            setDeleting(undefined);
        }
    };

    let content;
    if (loading && !exports.length) {
        content = <Spinner full/>
    }
    else {
        content = <>
            <h1>Data Exports</h1>
            <h4>Exports requested in the last 3 days. Older exports and their files are removed automatically.</h4>
            <AppHeader
                heading="Export List"
                icon={<SearchFilled/>}
                accent={ProjectAccents.CareCompliance}
            >
                <div
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: '12px',
                        justifyContent: 'space-between',
                    }}
                >
                    <Searchbox
                        onSearch={(e) => setSearchTerm(e)}
                        clearButton
                        style={{width: '100%'}}
                    />
                    {
                        workers.length
                            ? <Popover
                                popoverFor={
                                    <div style={{alignItems: 'center', cursor: 'pointer', display: 'flex', gap: '4px', whiteSpace: 'nowrap'}}>
                                        <ServerFilled/>
                                        <span>{busySlots}/{totalSlots}</span>
                                    </div>
                                }
                                placement="left"
                                background="dark"
                            >
                                <div style={{maxWidth: '400px'}}>
                                    {workers.map(worker => (
                                        <div key={worker.instanceId} style={{marginBottom: '4px'}}>
                                            {worker.busySlots}/{worker.totalSlots} slots busy
                                            {worker.isResponsive ? '' : ' — not responding, its slots are stuck'}
                                            <div>{worker.activeExports.length ? worker.activeExports.join(', ') : 'Idle'}</div>
                                        </div>
                                    ))}
                                </div>
                            </Popover>
                            : undefined
                    }
                </div>
            </AppHeader>
            {
                exports.length
                    ? <DataList
                        avatarProps={{placeholderField: 'initials'}}
                        primaryField="primaryFieldValue"
                        renderSubItemElements={(dataItem: DataExportJob) => (
                            <>
                                <ListItemElement icon={<DatabaseFilled accent={ProjectAccents.CareCompliance}/>} label="Corporate site:">
                                    {dataItem.corporateSiteName ?? 'Unknown'}
                                </ListItemElement>
                                <ListItemElement icon={<UserMaleLined accent={ProjectAccents.CareCompliance}/>} label="Requested by:">
                                    {dataItem.requestedBy ?? 'Unknown'}
                                </ListItemElement>
                                <ListItemElement icon={<ClockFilled accent={ProjectAccents.CareCompliance}/>} label="Requested:">
                                    {dataItem.requestedUtc ? moment.utc(dataItem.requestedUtc).fromNow() : 'Unknown'}
                                </ListItemElement>
                                <ListItemElement label={`Status: ${dataItem.status}`}>
                                    {
                                        dataItem.errorMessage
                                            ? undefined
                                            : <div style={{width: '100%'}}>
                                                <ProgressBar
                                                    value={dataItem.progress ?? 0}
                                                    accent={ProjectAccents.CareCompliance}
                                                />
                                                <div>{dataItem.secondaryProgress ?? ''}</div>
                                            </div>
                                    }
                                </ListItemElement>
                            </>
                        )}
                        renderRightElements={(dataItem: DataExportJob) => (
                            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                {
                                    dataItem.errorMessage
                                        ? <Popover
                                            popoverFor={<RoundButton icon={<CircleInfoFilled/>}/>}
                                            placement="left"
                                            background="dark"
                                        >
                                            <div style={{maxWidth: '600px', wordBreak: 'break-word'}}>
                                                {dataItem.errorMessage}
                                            </div>
                                        </Popover>
                                        : undefined
                                }
                                {
                                    isAdmin && dataItem.canCancel
                                        ? <Tooltip
                                            background={'dark'}
                                            placement={'left'}
                                            tooltipFor={
                                                <RoundButton
                                                    disabled={cancelling === dataItem.taskId}
                                                    onClick={() => cancel(dataItem)}
                                                    icon={<StopSignFilled/>}
                                                />
                                            }
                                        >
                                            Cancel export
                                        </Tooltip>
                                        : undefined
                                }
                                {
                                    dataItem.canDownload
                                        ? <Tooltip
                                            background={'dark'}
                                            placement={'left'}
                                            tooltipFor={
                                                <RoundButton
                                                    disabled={downloading === dataItem.taskId}
                                                    onClick={() => download(dataItem)}
                                                    icon={<DownloadFilled/>}
                                                />
                                            }
                                        >
                                            Download export
                                        </Tooltip>
                                        : undefined
                                }
                                {
                                    isAdmin && dataItem.canDelete
                                        ? <Tooltip
                                            background={'dark'}
                                            placement={'left'}
                                            tooltipFor={
                                                <RoundButton
                                                    disabled={deleting === dataItem.taskId}
                                                    onClick={() => remove(dataItem)}
                                                    icon={<DeleteFilled/>}
                                                />
                                            }
                                        >
                                            Delete export
                                        </Tooltip>
                                        : undefined
                                }
                            </div>
                        )}
                        data={exportsData}
                    />
                    : <h3>There are no recent data exports</h3>
            }
        </>
    }

    return (
        <>
            {content}

            {alert ?
                <Alert
                    className={styles.CustomerListAlert}
                    closeTransition="fade"
                    type={alert.type}
                    showDuration={1200}
                    onClosed={() => setAlert(undefined)}
                >
                    {alert.message}
                </Alert> : undefined
            }
        </>
    )
}

export default DataExports;
